// v10/src/components/admin/fuel/FuelMovementsTable.js
//
// Tabla paginada de movimientos de combustible con filtros.
// Filtros enviados como query params al backend:
//   movement_type, fuel_tank_id, aircraft_id, date_from, date_to, page, limit
// (todos soportados por GET /api/fuel-movements).

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Plus, Truck, SlidersHorizontal, RefreshCw, Loader,
  ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
  Filter, X as XIcon, History,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import api from '../../../services/apiService';
import fuelService from '../../../services/fuelService';
import { POLLING_INTERVALS } from '../../../config/realtime';
import useIsMobile from '../../../hooks/useIsMobile';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const TYPE_META = {
  intake:     { color: '#16a34a', bg: 'rgba(22,163,74,0.12)',  Icon: Plus,               sign: '+', labelKey: 'fuel_movement_type_intake' },
  dispatch:   { color: '#dc2626', bg: 'rgba(220,38,38,0.12)',  Icon: Truck,              sign: '-', labelKey: 'fuel_movement_type_dispatch' },
  adjustment: { color: '#ea580c', bg: 'rgba(234,88,12,0.12)',  Icon: SlidersHorizontal,  sign: '±', labelKey: 'fuel_movement_type_adjustment' },
};

const formatNum = (n, locale = 'es-AR') => {
  const num = Number(n);
  if (!Number.isFinite(num)) return '0';
  return num.toLocaleString(locale, { maximumFractionDigits: 2 });
};

const formatDateTime = (d, locale = 'es-AR') => {
  if (!d) return '—';
  const dt   = new Date(d);
  if (Number.isNaN(dt.getTime())) return '—';
  const date = dt.toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' });
  const time = `${String(dt.getHours()).padStart(2, '0')}:${String(dt.getMinutes()).padStart(2, '0')}`;
  return `${date} ${time}`;
};

const FuelMovementsTable = ({
  tanks = [],
  darkMode,
  locale = 'es-AR',
  isActive = true,
  refreshNonce = 0,
}) => {
  const { t } = useTranslation('admin');
  const isMobile = useIsMobile();

  const [movements, setMovements] = useState([]);
  const [total,    setTotal]      = useState(0);
  const [loading,  setLoading]    = useState(true);
  const [error,    setError]      = useState('');

  const [aircraftList, setAircraftList] = useState([]);

  const [page,      setPage]      = useState(1);
  const [pageSize,  setPageSize]  = useState(10);
  const [filters,   setFilters]   = useState({
    movement_type: '',
    fuel_tank_id:  '',
    aircraft_id:   '',
    date_from:     '',
    date_to:       '',
  });

  const pollRef = useRef(null);

  // Cargar aeronaves para el selector (solo una vez al montar).
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await api.get('/api/aircraft', { params: { is_active: 'true' } });
        if (mounted) setAircraftList(res?.data?.data || []);
      } catch (_) {
        if (mounted) setAircraftList([]);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Construir params para el backend
  const buildParams = useCallback(() => {
    const p = { page, limit: pageSize };
    if (filters.movement_type) p.movement_type = filters.movement_type;
    if (filters.fuel_tank_id)  p.fuel_tank_id  = filters.fuel_tank_id;
    if (filters.aircraft_id)   p.aircraft_id   = filters.aircraft_id;
    if (filters.date_from)     p.date_from     = filters.date_from;
    if (filters.date_to)       p.date_to       = filters.date_to;
    return p;
  }, [page, pageSize, filters]);

  const load = useCallback(async () => {
    setError('');
    try {
      const res = await fuelService.getMovements(buildParams());
      const body = res?.data || {};
      setMovements(body.data || []);
      setTotal(typeof body.total === 'number' ? body.total : 0);
    } catch (err) {
      setError(t('fuel_movements_table_load_error'));
    } finally {
      setLoading(false);
    }
  }, [buildParams, t]);

  // Carga inicial + cuando cambian filtros/paginación + cuando FuelTab pide refresh
  useEffect(() => {
    setLoading(true);
    load();
  }, [load, refreshNonce]);

  // Polling — pausado si la pestaña está inactiva, o si no hay registros
  useEffect(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (!isActive) return undefined;
    if (movements.length === 0) return undefined;

    pollRef.current = setInterval(load, POLLING_INTERVALS.maintenance);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    };
  }, [isActive, movements.length, load]);

  const setFilter = (k, v) => {
    setPage(1);
    setFilters((f) => ({ ...f, [k]: v }));
  };
  const clearFilters = () => {
    setPage(1);
    setFilters({
      movement_type: '', fuel_tank_id: '', aircraft_id: '',
      date_from: '', date_to: '',
    });
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const hasFilters = Object.values(filters).some((v) => v !== '');

  // Estilos compartidos
  const fgPrimary = darkMode ? 'white' : '#0f172a';
  const fgMuted   = darkMode ? 'rgba(255,255,255,0.5)'  : '#64748b';
  const fgFaint   = darkMode ? 'rgba(255,255,255,0.30)' : '#94a3b8';
  const cardBg    = darkMode ? 'rgba(255,255,255,0.03)' : 'white';
  const cardBorder = darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0';
  const inputStyle = {
    padding: '8px 12px', borderRadius: 10,
    border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0',
    background: darkMode ? 'rgba(255,255,255,0.05)' : 'white',
    color: fgPrimary, fontSize: 13, fontFamily: 'inherit',
    cursor: 'pointer', outline: 'none', minWidth: 0,
  };
  const headerThStyle = {
    padding: '10px 12px', textAlign: 'left',
    fontSize: 11, fontWeight: 800, textTransform: 'uppercase',
    letterSpacing: '0.06em', color: fgFaint,
    borderBottom: cardBorder,
    whiteSpace: 'nowrap',
  };
  const tdStyle = {
    padding: '12px', verticalAlign: 'middle',
    borderBottom: darkMode ? '1px solid rgba(255,255,255,0.05)' : '1px solid #f1f5f9',
    fontSize: 13, color: fgPrimary,
    whiteSpace: 'nowrap',
  };

  // ── Render fila por movimiento ───────────────────────────────────
  const renderRow = (mv) => {
    const meta = TYPE_META[mv.movement_type] || TYPE_META.adjustment;
    const Icon = meta.Icon;

    let signCh = meta.sign;
    if (mv.movement_type === 'adjustment') {
      // No sabemos direction explícito en el response del backend (no se devuelve);
      // El signo concreto sólo aplica para intake/dispatch.
      // Para adjustment dejamos ± con color naranja.
      signCh = '±';
    }

    let detail = '—';
    if (mv.movement_type === 'intake') {
      detail = mv.supplier || '—';
    } else if (mv.movement_type === 'dispatch') {
      const reg   = mv.aircraft_registration || '';
      const name  = mv.aircraft_name || '';
      detail = reg && name ? `${reg} · ${name}` : (reg || name || '—');
    } else {
      detail = mv.reason ? String(mv.reason).slice(0, 60) : '—';
    }

    const tankLabel = mv.tank_name
      ? `${mv.tank_name}${mv.fuel_type ? ` · ${mv.fuel_type}` : ''}`
      : (mv.fuel_type || '—');

    return (
      <tr key={mv.id}>
        <td style={tdStyle}>{formatDateTime(mv.created_at, locale)}</td>
        <td style={tdStyle}>
          <span
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '3px 10px', borderRadius: 999,
              background: meta.bg, color: meta.color,
              fontSize: 11, fontWeight: 800,
              whiteSpace: 'nowrap',
            }}
          >
            <Icon style={{ width: 12, height: 12 }} />
            {t(meta.labelKey)}
          </span>
        </td>
        <td style={tdStyle}>{tankLabel}</td>
        <td style={{ ...tdStyle, fontWeight: 800, color: meta.color, whiteSpace: 'nowrap' }}>
          {signCh}{formatNum(mv.quantity_liters, locale)} L
        </td>
        <td style={{ ...tdStyle, color: fgMuted }}>{detail}</td>
        <td style={{ ...tdStyle, color: fgMuted }}>{mv.recorded_by_name || '—'}</td>
      </tr>
    );
  };

  return (
    <div
      style={{
        display: 'flex', flexDirection: 'column', gap: 12,
        background: cardBg, border: cardBorder, borderRadius: 20,
        padding: 20, boxShadow: '0 2px 12px rgba(0,0,0,0.05)',
      }}
    >
      {/* Header de la sección */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
          <div
            style={{
              width: 36, height: 36, borderRadius: 12,
              background: darkMode ? 'rgba(99,102,241,0.18)' : 'rgba(99,102,241,0.10)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <History style={{ width: 18, height: 18, color: '#6366f1' }} />
          </div>
          <div style={{ minWidth: 0 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: fgPrimary, letterSpacing: '-0.2px' }}>
              {t('fuel_movements_table_title')}
            </h3>
            <p style={{ margin: '2px 0 0', fontSize: 12, color: fgMuted }}>
              {t('fuel_movements_table_total', { count: total })}
            </p>
          </div>
        </div>
        <motion.button
          type="button" onClick={load}
          whileTap={{ scale: 0.96 }}
          aria-label="refresh"
          style={{
            padding: 8, borderRadius: 10, border: 'none', cursor: 'pointer',
            background: darkMode ? 'rgba(255,255,255,0.06)' : '#f1f5f9',
            color: fgMuted, display: 'flex',
          }}
        >
          <RefreshCw style={{ width: 14, height: 14 }} />
        </motion.button>
      </div>

      {/* Filtros */}
      <div
        style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          flexWrap: isMobile ? 'nowrap' : 'wrap',
          gap: 8,
          alignItems: isMobile ? 'stretch' : 'center',
          padding: '10px 0', borderTop: cardBorder, borderBottom: cardBorder,
        }}
      >
        <span
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            fontSize: 11, fontWeight: 700, color: fgFaint, textTransform: 'uppercase',
            letterSpacing: '0.06em',
          }}
        >
          <Filter style={{ width: 12, height: 12 }} />
          {t('fuel_movements_table_filters')}
        </span>

        <select
          value={filters.movement_type}
          onChange={(e) => setFilter('movement_type', e.target.value)}
          style={{ ...inputStyle, width: isMobile ? '100%' : undefined, boxSizing: 'border-box' }}
        >
          <option value="">{t('fuel_movements_table_filter_type_all')}</option>
          <option value="intake">{t('fuel_movement_type_intake')}</option>
          <option value="dispatch">{t('fuel_movement_type_dispatch')}</option>
          <option value="adjustment">{t('fuel_movement_type_adjustment')}</option>
        </select>

        <select
          value={filters.fuel_tank_id}
          onChange={(e) => setFilter('fuel_tank_id', e.target.value)}
          style={{ ...inputStyle, width: isMobile ? '100%' : undefined, boxSizing: 'border-box' }}
        >
          <option value="">{t('fuel_movements_table_filter_tank_all')}</option>
          {tanks.map((tk) => (
            <option key={tk.id} value={tk.id}>
              {tk.name}{tk.fuel_type ? ` · ${tk.fuel_type}` : ''}
            </option>
          ))}
        </select>

        <select
          value={filters.aircraft_id}
          onChange={(e) => setFilter('aircraft_id', e.target.value)}
          style={{ ...inputStyle, width: isMobile ? '100%' : undefined, boxSizing: 'border-box' }}
        >
          <option value="">{t('fuel_movements_table_filter_aircraft_all')}</option>
          {aircraftList.map((a) => (
            <option key={a.id} value={a.id}>
              {a.registration || ''}{a.registration && (a.manufacturer || a.model_name) ? ' · ' : ''}{a.manufacturer || ''} {a.model_name || ''}
            </option>
          ))}
        </select>

        {isMobile ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 11, fontWeight: 700, color: fgFaint, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              {t('fuel_movements_table_filter_date_from')}
            </label>
            <input
              type="date"
              value={filters.date_from}
              onChange={(e) => setFilter('date_from', e.target.value)}
              aria-label={t('fuel_movements_table_filter_date_from')}
              style={{ ...inputStyle, cursor: 'text', width: '100%', boxSizing: 'border-box' }}
            />
          </div>
        ) : (
          <input
            type="date"
            value={filters.date_from}
            onChange={(e) => setFilter('date_from', e.target.value)}
            aria-label={t('fuel_movements_table_filter_date_from')}
            style={{ ...inputStyle, cursor: 'text' }}
          />
        )}

        {isMobile ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 11, fontWeight: 700, color: fgFaint, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              {t('fuel_movements_table_filter_date_to')}
            </label>
            <input
              type="date"
              value={filters.date_to}
              onChange={(e) => setFilter('date_to', e.target.value)}
              aria-label={t('fuel_movements_table_filter_date_to')}
              style={{ ...inputStyle, cursor: 'text', width: '100%', boxSizing: 'border-box' }}
            />
          </div>
        ) : (
          <input
            type="date"
            value={filters.date_to}
            onChange={(e) => setFilter('date_to', e.target.value)}
            aria-label={t('fuel_movements_table_filter_date_to')}
            style={{ ...inputStyle, cursor: 'text' }}
          />
        )}

        {hasFilters && (
          <button
            type="button" onClick={clearFilters}
            style={{
              display: 'inline-flex', alignItems: 'center',
              justifyContent: isMobile ? 'center' : undefined,
              gap: 6,
              padding: '6px 10px', borderRadius: 10, border: 'none',
              background: darkMode ? 'rgba(255,255,255,0.05)' : '#f1f5f9',
              color: fgMuted, cursor: 'pointer',
              fontSize: 12, fontWeight: 700, fontFamily: 'inherit',
              width: isMobile ? '100%' : undefined, boxSizing: 'border-box',
            }}
          >
            <XIcon style={{ width: 12, height: 12 }} />
            {t('fuel_movements_table_clear_filters')}
          </button>
        )}
      </div>

      {/* Tabla / estados */}
      {loading && movements.length === 0 ? (
        <div
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            padding: '32px 0', color: fgMuted, fontSize: 13,
          }}
        >
          <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
          <Loader style={{ width: 16, height: 16, animation: 'spin 1s linear infinite' }} />
          {t('fuel_movements_table_loading')}
        </div>
      ) : error ? (
        <p style={{ margin: 0, padding: 16, color: '#ef4444', fontSize: 13, fontWeight: 600 }}>
          {error}
        </p>
      ) : movements.length === 0 ? (
        <div
          style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', gap: 8, padding: '32px 16px',
            color: fgMuted, textAlign: 'center',
          }}
        >
          <History style={{ width: 28, height: 28, color: fgFaint }} />
          <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: fgPrimary }}>
            {t('fuel_movements_table_empty_title')}
          </p>
          <p style={{ margin: 0, fontSize: 12, color: fgMuted, maxWidth: 420 }}>
            {t('fuel_movements_table_empty_desc')}
          </p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table
            style={{
              width: '100%', borderCollapse: 'collapse',
              fontFamily: 'inherit',
            }}
          >
            <thead>
              <tr>
                <th style={headerThStyle}>{t('fuel_movements_table_col_date')}</th>
                <th style={headerThStyle}>{t('fuel_movements_table_col_type')}</th>
                <th style={headerThStyle}>{t('fuel_movements_table_col_tank')}</th>
                <th style={headerThStyle}>{t('fuel_movements_table_col_qty')}</th>
                <th style={headerThStyle}>{t('fuel_movements_table_col_detail')}</th>
                <th style={headerThStyle}>{t('fuel_movements_table_col_user')}</th>
              </tr>
            </thead>
            <tbody>
              {movements.map(renderRow)}
            </tbody>
          </table>
        </div>
      )}

      {/* Paginación */}
      {movements.length > 0 && (
        <div
          style={{
            display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8,
            justifyContent: 'space-between', paddingTop: 8,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: fgMuted }}>
            <span>{t('fuel_movements_table_page_size')}</span>
            <select
              value={pageSize}
              onChange={(e) => { setPage(1); setPageSize(Number(e.target.value)); }}
              style={{ ...inputStyle, padding: '6px 8px', fontSize: 12 }}
            >
              {PAGE_SIZE_OPTIONS.map((sz) => <option key={sz} value={sz}>{sz}</option>)}
            </select>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <button
              type="button" onClick={() => setPage(1)} disabled={page === 1}
              aria-label="first" style={{
                padding: 6, borderRadius: 8, border: 'none',
                background: 'transparent', color: page === 1 ? fgFaint : fgMuted,
                cursor: page === 1 ? 'not-allowed' : 'pointer',
              }}
            >
              <ChevronsLeft style={{ width: 14, height: 14 }} />
            </button>
            <button
              type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}
              aria-label="previous" style={{
                padding: 6, borderRadius: 8, border: 'none',
                background: 'transparent', color: page === 1 ? fgFaint : fgMuted,
                cursor: page === 1 ? 'not-allowed' : 'pointer',
              }}
            >
              <ChevronLeft style={{ width: 14, height: 14 }} />
            </button>
            <span style={{ fontSize: 12, color: fgMuted, padding: '0 6px' }}>
              {t('fuel_movements_table_page_of', { current: page, total: totalPages })}
            </span>
            <button
              type="button" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
              aria-label="next" style={{
                padding: 6, borderRadius: 8, border: 'none',
                background: 'transparent', color: page >= totalPages ? fgFaint : fgMuted,
                cursor: page >= totalPages ? 'not-allowed' : 'pointer',
              }}
            >
              <ChevronRight style={{ width: 14, height: 14 }} />
            </button>
            <button
              type="button" onClick={() => setPage(totalPages)} disabled={page >= totalPages}
              aria-label="last" style={{
                padding: 6, borderRadius: 8, border: 'none',
                background: 'transparent', color: page >= totalPages ? fgFaint : fgMuted,
                cursor: page >= totalPages ? 'not-allowed' : 'pointer',
              }}
            >
              <ChevronsRight style={{ width: 14, height: 14 }} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FuelMovementsTable;
